import React, { useEffect, useState, useMemo } from 'react';
import { SectionHeader } from '../components/UI';
import { api, moneyFull, pct } from '../lib/api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts';
import { ArrowUpRight, ArrowDownRight, AlertTriangle, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { TimeSeriesTile } from '../components/sales/TimeSeriesTile';
import { ClockLabel } from '../components/sales/ClockLabel';

// Hoisted chart styles — stable references across renders.
const CHART_MARGIN = { left: 10, right: 10, top: 10, bottom: 10 };
const CHART_GRID_STROKE = { strokeDasharray: '2 4', stroke: '#141B4D', strokeOpacity: 0.1 };
const CHART_TOOLTIP_STYLE = { background: '#FFFFFF', border: '1px solid #141B4D', color: '#141B4D', borderRadius: 0, boxShadow: '0 4px 12px rgba(20,27,77,0.12)', fontSize: 12 };
const CHART_TOOLTIP_LABEL = { color: '#141B4D', fontWeight: 600, marginBottom: 4 };
const CHART_TOOLTIP_ITEM = { color: '#141B4D' };

// Compare current MTD to an equivalent day-of-month comparison point (same
// DoM last month or last year). Guards against nulls (missing prior data).
function Trend({ current, prior, formatter = (v) => v, label, priorNote }) {
  if (prior === undefined || prior === null || typeof current !== 'number' || typeof prior !== 'number') return null;
  const diff = current - prior;
  if (Number.isNaN(diff)) return null;
  const up = diff >= 0;
  return (
    <div className={`inline-flex items-center gap-1 text-xs ${up ? 'text-indie-pos-dark' : 'text-indie-neg-dark'}`}>
      {up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
      <span className="num-tabular">{formatter(Math.abs(diff))}</span>
      <span className="text-indie-ink/50">{label}{priorNote ? ` · ${priorNote}` : ''}</span>
    </div>
  );
}

// Format YYYY-MM-DD to short "Jul 7" or fallback to raw string.
function shortDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(`${iso}T00:00:00Z`);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
  } catch {
    return iso;
  }
}

export default function DailyPulse() {
  const [today, setToday] = useState(null);
  const [insights, setInsights] = useState(null);
  const [ts, setTs] = useState(null);
  const [mtdSeries, setMtdSeries] = useState(null);
  const [overlayMetric, setOverlayMetric] = useState('revenue_mtd');

  useEffect(() => {
    api.get('/daily-pulse/today').then((r) => setToday(r.data)).catch(() => {});
    api.get('/daily-pulse/insights').then((r) => setInsights(r.data)).catch(() => {});
    api.get('/sales/timeseries').then((r) => setTs(r.data)).catch(() => {});
    api.get('/sales/console/mtd').then((r) => setMtdSeries(r.data)).catch(() => {});
  }, []);

  if (!today) return <div className="label-editorial">Loading…</div>;
  const t = today.today;
  const p = today.prior;
  const pm = today.prior_month_dom;   // same day-of-month last month
  const py = today.prior_year_dom;    // same day-of-month last year

  // Staleness flag — if the latest post is >= 2 days old, the panels below
  // are showing an older snapshot, not truly today's. Surface that instead
  // of silently rendering stale data as if it were current.
  const latestDate = t?.date ? new Date(`${t.date}T00:00:00Z`) : new Date();
  const nowUtc = new Date();
  const staleDays = Math.floor((nowUtc.getTime() - latestDate.getTime()) / 86400000);
  const isStale = staleDays >= 2;

  return (
    <div className="space-y-10">
      <SectionHeader
        eyebrow="Daily Pulse · #dailyhighlight"
        title="Today across the business"
        description={`Parsed from the latest #dailyhighlight post · ${new Date(t.posted_at).toLocaleString()}`}
      />

      {/* Period-source explainer — clarifies that finalized cash-clock tiles
          rely on closed-month sources while the Slack-based sections below
          reflect current-period activity. Without this, "Today" framing at
          the top can look like it conflicts with month-lagged financials. */}
      <div
        data-testid="pulse-period-notice"
        className="bg-indie-cream border-l-4 border-indie-navy px-4 py-3 text-sm text-indie-ink/80 leading-relaxed"
      >
        <span className="font-semibold text-indie-navy">Reading this page:</span>{' '}
        The metric charts and MTD hero below reflect live current-period activity from{' '}
        <span className="font-mono text-xs">#dailyhighlight</span> (updated hourly).{' '}
        Any finalized cash-revenue and BvA tiles elsewhere in the app are anchored to the last closed month —
        QBO and the Corrected BvA sheet only publish after month-end. That&apos;s why the &ldquo;today&rdquo; framing
        here can differ from month-lagged financials on Dashboard or Budget vs Actuals.
      </div>

      {isStale && (
        <div data-testid="pulse-stale-banner" className="bg-indie-yellow/25 border-l-4 border-indie-yellow p-4 flex items-start gap-3">
          <AlertTriangle className="text-indie-ink shrink-0 mt-0.5" size={18} />
          <div className="text-sm text-indie-ink">
            <strong>Slack sync appears stale.</strong> Latest #dailyhighlight post is {staleDays} days old ({shortDate(t.date)}).
            The panels below reflect that day, not today. Ask an admin to re-run the Slack sync.
          </div>
        </div>
      )}

      {/* Metric-line small-multiples (P1) — every KPI is a line over time now.
          Reuses the shared TimeSeriesTile from sales/, sourced from
          /api/sales/timeseries (monthly, all closed months). Dashed goal =
          plan, solid = actual, MoM delta above every chart. */}
      {ts?.metrics && (
        <div data-testid="pulse-metric-grid" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {['revenue', 'sales_count', 'calls_booked', 'calls_held', 'qualification_rate', 'close_rate', 'no_show_rate', 'avg_deal_price']
            .map((k) => (ts.metrics || []).find((m) => m.key === k))
            .filter(Boolean)
            .map((m) => (
              <TimeSeriesTile
                key={m.key}
                metric={m}
                methodologyBreaks={ts.methodology_breaks}
                color="#141B4D"
                goalColor="#1D57A5"
                compact
                testId={`pulse-metric-${m.key}`}
              />
            ))}
        </div>
      )}

      {/* Hero cumulative-MTD chart (P2) — day-by-day MTD revenue vs prior
          month, on one canvas. Replaces the static navy snapshot as the
          dominant intra-month view. Uses `series[]` from /sales/console/mtd. */}
      {mtdSeries?.series?.length > 0 && (() => {
        const mtdVal = t.monthly_revenue_to_date;
        const priorFinal = mtdSeries.prior_month_final;
        const lookbackFinal = mtdSeries.lookback_final;
        const lookbackLabel = mtdSeries.lookback_month_label;
        const lookbackMonthsBack = mtdSeries.lookback_months_back;
        const lookbackHasDaily = mtdSeries.lookback_has_daily;
        const priorDelta = priorFinal ? (mtdVal - priorFinal) / priorFinal : null;
        const lookbackDelta = lookbackFinal ? (mtdVal - lookbackFinal) / lookbackFinal : null;
        const lookbackCardLabel = lookbackMonthsBack ? `vs ${lookbackMonthsBack} months ago · ${lookbackLabel}` : null;
        return (
        <div className="bg-white border-t-4 border-indie-navy p-6" data-testid="pulse-mtd-hero">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <TrendingUp size={14} className="text-indie-ink/70" strokeWidth={1.5} />
              <div className="text-[10px] uppercase tracking-[0.16em] text-indie-ink/60 font-semibold">
                {mtdSeries.month_label || 'This month'} · cumulative revenue by day
              </div>
              <ClockLabel type="cash" />
            </div>
            <div className="text-[11px] text-indie-ink/60 num-tabular">
              Day {mtdSeries.latest?.day} · <span className="font-semibold text-indie-ink">{moneyFull(mtdVal)}</span> MTD
            </div>
          </div>

          {/* Comparison strip — MTD vs prior month vs adaptive lookback
              (6 mo when daily data available, else 3 mo). */}
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="pulse-mtd-comparison-strip">
            <div className="border border-indie-navy/20 px-3 py-2">
              <div className="text-[10px] uppercase tracking-[0.14em] text-indie-ink/55 font-semibold">MTD · {mtdSeries.month_label}</div>
              <div className="text-lg font-semibold text-indie-navy num-tabular">{moneyFull(mtdVal)}</div>
              <div className="text-[10px] text-indie-ink/60">Day {mtdSeries.latest?.day} of month</div>
            </div>
            <div className="border-l-4 border-indie-neg-dark/60 border-t border-r border-b border-indie-ink/10 px-3 py-2 bg-indie-cream/40" data-testid="pulse-mtd-vs-prior">
              <div className="text-[10px] uppercase tracking-[0.14em] text-indie-ink/55 font-semibold">vs prior month · {mtdSeries.prior_month_label}</div>
              <div className="text-lg font-semibold text-indie-navy num-tabular">
                {priorFinal != null ? moneyFull(priorFinal) : '—'}
                {priorDelta != null && (
                  <span className={`ml-2 text-xs ${priorDelta >= 0 ? 'text-indie-pos-dark' : 'text-indie-neg-dark'}`}>
                    {priorDelta >= 0 ? '+' : ''}{(priorDelta * 100).toFixed(1)}%
                  </span>
                )}
              </div>
              <div className="text-[10px] text-indie-ink/60">Month final · cash clock</div>
            </div>
            {lookbackFinal != null && (
              <div className="border-l-4 border-indie-cobalt/70 border-t border-r border-b border-indie-ink/10 px-3 py-2 bg-indie-cream/40" data-testid="pulse-mtd-vs-lookback">
                <div className="text-[10px] uppercase tracking-[0.14em] text-indie-ink/55 font-semibold">{lookbackCardLabel}</div>
                <div className="text-lg font-semibold text-indie-navy num-tabular">
                  {moneyFull(lookbackFinal)}
                  {lookbackDelta != null && (
                    <span className={`ml-2 text-xs ${lookbackDelta >= 0 ? 'text-indie-pos-dark' : 'text-indie-neg-dark'}`}>
                      {lookbackDelta >= 0 ? '+' : ''}{(lookbackDelta * 100).toFixed(1)}%
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-indie-ink/60">
                  {lookbackHasDaily ? 'Day-by-day overlaid on chart' : 'Month final only — daily curve unavailable'}
                  {lookbackMonthsBack === 3 && ' · 6-mo lookback pending Feb 2026 backfill'}
                </div>
              </div>
            )}
          </div>

          <div className="h-64 mt-3">
            <ResponsiveContainer>
              <LineChart data={mtdSeries.series} margin={CHART_MARGIN}>
                <CartesianGrid {...CHART_GRID_STROKE} />
                <XAxis dataKey="day" stroke="#101820" fontSize={11} tickLine={false} />
                <YAxis stroke="#101820" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
                <Tooltip contentStyle={CHART_TOOLTIP_STYLE} labelStyle={CHART_TOOLTIP_LABEL} itemStyle={CHART_TOOLTIP_ITEM} formatter={(v) => v == null ? '—' : moneyFull(v)} labelFormatter={(l) => `Day ${l}`} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="mtd_revenue" name={`${mtdSeries.month_label || 'This month'} · cumulative`} stroke="#141B4D" strokeWidth={3} dot={{ r: 3 }} connectNulls />
                <Line type="monotone" dataKey="prior" name={`${mtdSeries.prior_month_label || 'Prior month'} · same day`} stroke="#B4530C" strokeWidth={2} strokeDasharray="4 3" dot={false} connectNulls />
                {lookbackHasDaily && (
                  <Line type="monotone" dataKey="lookback" name={`${lookbackLabel || 'Lookback'} · same day (${lookbackMonthsBack} mo ago)`} stroke="#1D57A5" strokeWidth={2} strokeDasharray="2 4" dot={false} connectNulls />
                )}
                {mtdSeries.baseline_target != null && (
                  <ReferenceLine y={mtdSeries.baseline_target} stroke="#006747" strokeDasharray="5 4" strokeOpacity={0.8} label={{ value: `Baseline ${moneyFull(mtdSeries.baseline_target)}`, position: 'insideTopRight', fontSize: 10, fill: '#006747' }} />
                )}
                {!lookbackHasDaily && lookbackFinal != null && (
                  <ReferenceLine y={lookbackFinal} stroke="#1D57A5" strokeDasharray="2 4" strokeOpacity={0.8} label={{ value: `${lookbackMonthsBack} mo ago · ${lookbackLabel} final ${moneyFull(lookbackFinal)}`, position: 'insideBottomLeft', fontSize: 10, fill: '#1D57A5' }} />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
          {lookbackLabel && (
            <div className="mt-2 text-[10px] text-indie-ink/50">
              Comparison reference: {lookbackLabel} ({lookbackMonthsBack} mo ago{lookbackHasDaily ? ', day-by-day' : `, final only $${Math.round((lookbackFinal || 0) / 1000)}k`}). History starts Jan 2026 — 6-mo daily curve populates as backfill completes; YoY overlay unlocks Jan 2027.
            </div>
          )}
        </div>
        );
      })()}

      {/* Snapshot metadata + raw Slack post — kept per user choice 3a as the
          audit trail for the parsed metrics above. */}
      <div className="bg-indie-navy text-indie-cream p-6" data-testid="pulse-snapshot-block">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <div>
            <div className="label-editorial-cream">Snapshot · {new Date(t.posted_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</div>
            <div className="mt-2 text-sm text-indie-cream/80">
              Latest <span className="font-mono">#dailyhighlight</span> post — parsed into the metric charts above and synced hourly.
              {typeof p?.monthly_revenue_to_date === 'number' && (
                <> Since yesterday: <span className="text-white num-tabular font-semibold">+{moneyFull(Math.max(0, t.monthly_revenue_to_date - p.monthly_revenue_to_date))}</span> and <span className="text-white num-tabular font-semibold">+{Math.max(0, t.sales_count - p.sales_count)}</span> new sales.</>
              )}
            </div>
          </div>
        </div>

        <details className="mt-5 pt-5 border-t border-indie-cream/15 text-xs text-indie-cream/70">
          <summary className="cursor-pointer hover:text-white transition-colors uppercase tracking-wider text-[11px] font-semibold">Show raw Slack post</summary>
          <div className="mt-3 text-indie-cream/80 text-xs leading-relaxed whitespace-pre-wrap font-mono">
            {t.raw_text}
          </div>
        </details>
      </div>

      {/* --- Pace vs. goal (lines now) + overlay + funnel ------------------
          Advisor Feb 2026 · phase-2 (line-first): the horizontal bars in
          PaceVsGoal became mini day-by-day line charts. Everything else
          stays.
      ---------------------------------------------------------------------- */}
      {insights ? (
        <>
          <PaceVsGoalRow pace={insights.pace} asOf={insights.as_of} mtdDays={mtdSeries?.series} mtdSeries={mtdSeries} />
          <OverlayChart
            overlay={insights.overlay}
            metric={overlayMetric}
            setMetric={setOverlayMetric}
            currentDayOfMonth={insights.pace?.day_of_month}
          />
          <FunnelSnapshot funnel={insights.funnel} />
        </>
      ) : (
        <div className="text-[11px] text-indie-ink/50">Loading insights…</div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PaceVsGoal — "are we on track?" widget. Two cards side by side: revenue and
// sales. Each shows MTD actual, MTD pace target at this day-of-month, and a
// naive linear projection of end-of-month landing.
// ---------------------------------------------------------------------------
const STATUS_STYLES = {
  on_pace:   { label: 'On pace',  color: 'text-indie-pos-dark', bg: 'bg-indie-pos/[0.10]', bar: 'bg-indie-pos-dark',  Icon: TrendingUp },
  watch:     { label: 'Watch',    color: 'text-indie-yellow-ink', bg: 'bg-indie-yellow/25', bar: 'bg-indie-yellow',   Icon: Minus },
  behind:    { label: 'Behind',   color: 'text-indie-neg-dark', bg: 'bg-indie-neg/[0.10]', bar: 'bg-indie-neg-dark',  Icon: TrendingDown },
  no_target: { label: 'No target',color: 'text-indie-ink/60',   bg: 'bg-indie-cream',      bar: 'bg-indie-ink/30',   Icon: Minus },
};

function PaceVsGoalRow({ pace, asOf, mtdDays, mtdSeries }) {
  if (!pace) return null;
  const cards = [
    {
      key: 'revenue',
      label: 'Revenue pace',
      mtd: pace.mtd_revenue,
      target: pace.revenue_target,
      paceTarget: pace.revenue_pace_target,
      projected: pace.revenue_projected_eom,
      status: pace.revenue_status,
      fmt: moneyFull,
      actualKey: 'cumulative_revenue',
      goalKey: 'prorated_target',
    },
    {
      key: 'sales',
      label: 'Sales pace',
      mtd: pace.mtd_sales,
      target: pace.sales_target,
      paceTarget: pace.sales_pace_target,
      projected: pace.sales_projected_eom,
      status: pace.sales_status,
      fmt: (v) => Number(v || 0).toFixed(0),
      actualKey: 'cumulative_sales',
      goalKey: 'prorated_sales_target',
    },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="pulse-pace-row">
      {cards.map((c) => {
        const s = STATUS_STYLES[c.status] || STATUS_STYLES.no_target;
        const Icon = s.Icon;
        const actualPct = c.target ? Math.min(1, c.mtd / c.target) : 0;
        // P3 · line-first: revenue pace shows day-by-day cumulative from
        // the /sales/console/mtd `series[]`. Sales pace falls back to
        // point-in-time until day-by-day sales counts land in that
        // endpoint.
        const dayRows = c.key === 'revenue' ? (mtdDays || []).map((d) => ({
          day: d.day,
          actual: d.mtd_revenue,
          prior: d.prior,
          lookback: d.lookback,
        })) : [];
        const lookbackHasDaily = c.key === 'revenue' && mtdSeries?.lookback_has_daily;
        const lookbackLabel = mtdSeries?.lookback_month_label;
        const lookbackMonthsBack = mtdSeries?.lookback_months_back;
        const lookbackFinal = mtdSeries?.lookback_final;
        return (
          <div key={c.key} data-testid={`pulse-pace-${c.key}`} className="p-6 border-t-4 border-indie-navy bg-white">
            <div className="flex items-baseline justify-between">
              <div>
                <div className="label-editorial">{c.label}</div>
                <div className="mt-1 text-xs text-indie-ink/60">
                  Day {pace.day_of_month} of {pace.days_in_month}
                  {c.target ? ` · Goal ${c.fmt(c.target)}` : ' · No BHAG target set'}
                </div>
              </div>
              <div className={`inline-flex items-center gap-1.5 px-2 py-1 text-[11px] font-semibold uppercase tracking-wider ${s.bg} ${s.color}`}>
                <Icon size={12} /> {s.label}
              </div>
            </div>

            <div className="mt-4 flex items-baseline gap-3">
              <span className="text-3xl font-semibold text-indie-navy num-tabular">{c.fmt(c.mtd)}</span>
              <span className="text-xs text-indie-ink/60">
                of {c.fmt(c.target || 0)} target
                {c.target ? ` · ${(actualPct * 100).toFixed(0)}%` : ''}
              </span>
            </div>

            {dayRows.length > 0 ? (
              <div className="mt-4 h-28" data-testid={`pulse-pace-line-${c.key}`}>
                <ResponsiveContainer>
                  <LineChart data={dayRows} margin={{ left: 0, right: 5, top: 5, bottom: 0 }}>                    <CartesianGrid {...CHART_GRID_STROKE} />
                    <XAxis dataKey="day" stroke="#101820" fontSize={9} tickLine={false} axisLine={false} />
                    <YAxis stroke="#101820" fontSize={9} tickLine={false} axisLine={false} width={35} tickFormatter={(v) => c.key === 'revenue' ? `$${Math.round(v / 1000)}k` : Math.round(v).toString()} />
                    <Tooltip contentStyle={CHART_TOOLTIP_STYLE} labelStyle={CHART_TOOLTIP_LABEL} itemStyle={CHART_TOOLTIP_ITEM} formatter={(v) => v == null ? '—' : c.fmt(v)} labelFormatter={(l) => `Day ${l}`} />
                    <Line type="monotone" dataKey="actual" name="This month" stroke="#141B4D" strokeWidth={2.5} dot={false} connectNulls />
                    <Line type="monotone" dataKey="prior" name="Prior month" stroke="#B4530C" strokeWidth={2} strokeDasharray="4 3" dot={false} connectNulls />
                    {lookbackHasDaily && (
                      <Line type="monotone" dataKey="lookback" name={`${lookbackLabel || 'Lookback'} (${lookbackMonthsBack} mo ago)`} stroke="#1D57A5" strokeWidth={2} strokeDasharray="2 4" dot={false} connectNulls />
                    )}
                    {!lookbackHasDaily && c.key === 'revenue' && lookbackFinal != null && (
                      <ReferenceLine y={lookbackFinal} stroke="#1D57A5" strokeDasharray="2 4" strokeOpacity={0.6} />
                    )}
                    <ReferenceLine x={pace.day_of_month} stroke="#006747" strokeDasharray="2 3" strokeOpacity={0.6} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="mt-4 text-[11px] text-indie-ink/40 italic">
                Day-by-day pace data unavailable yet — falling back to point-in-time reading.
              </div>
            )}

            <div className="mt-2 flex items-center justify-between text-[11px] text-indie-ink/55">
              <span>Pace target today: <span className="text-indie-navy font-medium num-tabular">{c.fmt(c.paceTarget)}</span></span>
              <span>Projecting EOM: <span className={`${s.color} font-medium num-tabular`}>{c.fmt(c.projected)}</span></span>
            </div>
          </div>
        );
      })}
      <div className="col-span-full text-[11px] text-indie-ink/50 -mt-2">
        Pace + projection anchored to today ({asOf}). Target source: {pace.target_source || 'not configured — set BHAG monthly targets to enable'}. Solid navy = cumulative MTD · orange dashed = prior month · blue dotted = {mtdSeries?.lookback_months_back ? `${mtdSeries.lookback_months_back} months ago (${mtdSeries.lookback_month_label})` : 'lookback'} · green guide = today. Projection is a linear extrapolation from current MTD.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// OverlayChart — 3-line comparison of MTD progression across current month,
// prior month, and 12 months prior. Metric switcher for revenue / sales /
// calls. Vertical reference line marks today's day-of-month.
// ---------------------------------------------------------------------------
const OVERLAY_METRICS = [
  { key: 'revenue_mtd', label: 'Revenue MTD', fmt: (v) => `$${Math.round(v / 1000)}k` },
  { key: 'sales_mtd',   label: 'Sales MTD',   fmt: (v) => Number(v).toFixed(0) },
  { key: 'calls_mtd',   label: 'Calls booked MTD', fmt: (v) => Number(v).toFixed(0) },
];

function OverlayChart({ overlay, metric, setMetric, currentDayOfMonth }) {
  const active = OVERLAY_METRICS.find((m) => m.key === metric) || OVERLAY_METRICS[0];

  // Join the three month series by day-of-month so recharts can plot them
  // on the same X axis (day 1 → 31).
  const data = useMemo(() => {
    const rows = {};
    const push = (series, key) => {
      (series || []).forEach((row) => {
        if (!rows[row.day]) rows[row.day] = { day: row.day };
        rows[row.day][key] = row[metric];
      });
    };
    push(overlay?.current_month?.series, 'current');
    push(overlay?.prior_month?.series, 'prior_month');
    push(overlay?.prior_year?.series, 'prior_year');
    const maxDay = Math.max(...Object.keys(rows).map(Number), currentDayOfMonth || 1);
    const arr = [];
    for (let d = 1; d <= maxDay; d += 1) {
      arr.push(rows[d] || { day: d });
    }
    return arr;
  }, [overlay, metric, currentDayOfMonth]);

  const hasPriorYear = (overlay?.prior_year?.series?.length || 0) > 0;

  return (
    <div className="bg-white border-t-4 border-indie-navy p-6" data-testid="pulse-overlay-chart">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <div className="label-editorial">Same day-of-month overlay</div>
          <div className="text-xs text-indie-ink/60 mt-1">
            How this month is tracking against the prior month{hasPriorYear ? ' and the same month last year' : ''} — measured at the same day-of-month so you can compare apples-to-apples.
          </div>
        </div>
        <div className="inline-flex items-center gap-0 border border-indie-navy/20" data-testid="overlay-metric-switcher">
          {OVERLAY_METRICS.map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => setMetric(m.key)}
              data-testid={`overlay-metric-${m.key}`}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-wider font-semibold border-r border-indie-navy/20 last:border-r-0 ${
                metric === m.key ? 'bg-indie-navy text-indie-cream' : 'bg-white text-indie-ink hover:bg-indie-cream'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      <div className="h-72 mt-5">
        <ResponsiveContainer>
          <LineChart data={data} margin={CHART_MARGIN}>
            <CartesianGrid {...CHART_GRID_STROKE} />
            <XAxis
              dataKey="day"
              stroke="#101820"
              fontSize={11}
              tickLine={false}
              label={{ value: 'Day of month', position: 'insideBottom', offset: -2, fontSize: 10, fill: '#141B4D80' }}
            />
            <YAxis stroke="#101820" fontSize={11} tickLine={false} axisLine={false} tickFormatter={active.fmt} />
            <Tooltip
              contentStyle={CHART_TOOLTIP_STYLE}
              labelStyle={CHART_TOOLTIP_LABEL}
              itemStyle={CHART_TOOLTIP_ITEM}
              formatter={(v) => (typeof v === 'number' ? active.fmt(v) : v)}
              labelFormatter={(day) => `Day ${day} of month`}
            />
            <Legend
              verticalAlign="top"
              align="right"
              iconType="plainline"
              wrapperStyle={{ fontSize: 11, top: -12 }}
            />
            {currentDayOfMonth && (
              <ReferenceLine
                x={currentDayOfMonth}
                stroke="#141B4D"
                strokeDasharray="4 4"
                label={{ value: 'today', position: 'top', fontSize: 10, fill: '#141B4D' }}
              />
            )}
            {hasPriorYear && (
              <Line
                type="monotone"
                dataKey="prior_year"
                name={overlay?.prior_year?.label || 'Prior year'}
                stroke="#141B4D"
                strokeWidth={1.5}
                strokeDasharray="4 4"
                dot={false}
                connectNulls
              />
            )}
            <Line
              type="monotone"
              dataKey="prior_month"
              name={overlay?.prior_month?.label || 'Prior month'}
              stroke="#1D57A5"
              strokeWidth={2}
              dot={false}
              connectNulls
            />
            <Line
              type="monotone"
              dataKey="current"
              name={overlay?.current_month?.label || 'This month'}
              stroke="#006747"
              strokeWidth={3}
              dot={{ r: 3, fill: '#006747' }}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {!hasPriorYear && (
        <div className="text-[11px] text-indie-ink/45 italic mt-1">
          No prior-year data — the #dailyhighlight archive doesn&#39;t reach {overlay?.prior_year?.label}. The 12-month comparison will populate as history builds up.
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FunnelSnapshot — Ad Spend → Calls booked → Qualified → Sales, with %
// drop-off at each stage and a side-by-side comparison to the same day-of-
// month last month. This is the "why" when the pace widget goes yellow.
// ---------------------------------------------------------------------------
function FunnelSnapshot({ funnel }) {
  if (!funnel?.current) return null;
  const c = funnel.current;
  const p = funnel.prior_month_dom;
  const priorDate = funnel.prior_month_dom_date;

  const stages = [
    { key: 'calls_booked', label: 'Calls booked', value: c.calls_booked, prior: p?.calls_booked, denom: null, fmt: (v) => Number(v).toFixed(0) },
    { key: 'qualified_calls', label: 'Qualified', value: c.qualified_calls, prior: p?.qualified_calls, denom: c.calls_booked, priorDenom: p?.calls_booked, fmt: (v) => Number(v).toFixed(0) },
    { key: 'sales', label: 'Sales', value: c.sales, prior: p?.sales, denom: c.qualified_calls, priorDenom: p?.qualified_calls, fmt: (v) => Number(v).toFixed(0) },
  ];
  const maxCount = Math.max(c.calls_booked, 1);

  return (
    <div className="bg-white border-t-4 border-indie-navy p-6" data-testid="pulse-funnel">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <div className="label-editorial">Sales funnel — this month vs. last month</div>
          <div className="text-xs text-indie-ink/60 mt-1">
            Each stage: MTD count, % conversion from the previous stage, and comparison to last month{priorDate ? ` (same DoM, ${priorDate})` : ''}. When the pace widget goes yellow, this is where the leak usually shows up.
          </div>
        </div>
        <div className="text-right text-xs text-indie-ink/70">
          <div>Ad spend: <span className="num-tabular font-semibold text-indie-navy">{moneyFull(c.ad_spend)}</span></div>
          <div className="mt-0.5">ROAS: <span className="num-tabular font-semibold text-indie-evergreen">{c.roas.toFixed(2)}x</span></div>
        </div>
      </div>

      <div className="mt-6 space-y-4">
        {stages.map((s, idx) => {
          const currRate = s.denom ? s.value / s.denom : null;
          const priorRate = s.priorDenom ? s.prior / s.priorDenom : null;
          const width = (s.value / maxCount) * 100;
          const priorWidth = p ? (s.prior / maxCount) * 100 : 0;
          const delta = p && typeof s.prior === 'number' ? s.value - s.prior : null;
          const rateDelta = currRate != null && priorRate != null ? currRate - priorRate : null;
          return (
            <div key={s.key} data-testid={`pulse-funnel-stage-${s.key}`} className="grid grid-cols-12 gap-3 items-center">
              <div className="col-span-3">
                <div className="text-xs uppercase tracking-wider text-indie-ink/70 font-semibold">{s.label}</div>
                {s.denom != null && (
                  <div className="text-[10px] text-indie-ink/50 mt-0.5">
                    from {stages[idx - 1].label.toLowerCase()}
                  </div>
                )}
              </div>
              <div className="col-span-6">
                <div className="relative h-6 bg-indie-cream border border-indie-navy/10">
                  {p && (
                    <div
                      className="absolute top-0 bottom-0 bg-indie-cobalt/25"
                      style={{ width: `${priorWidth}%` }}
                      title={`Last month DoM: ${s.fmt(s.prior || 0)}`}
                    />
                  )}
                  <div
                    className="absolute top-0 bottom-0 bg-indie-evergreen/85 flex items-center justify-end pr-2"
                    style={{ width: `${width}%` }}
                  >
                    <span className="text-white text-xs font-semibold num-tabular">{s.fmt(s.value)}</span>
                  </div>
                </div>
              </div>
              <div className="col-span-3 text-xs text-indie-ink/70">
                {currRate != null && (
                  <div className="num-tabular">
                    <span className="text-indie-navy font-semibold">{pct(currRate)}</span>
                    <span className="text-indie-ink/50 ml-1">conversion</span>
                  </div>
                )}
                {delta != null && (
                  <div className={`text-[11px] mt-0.5 ${delta > 0 ? 'text-indie-pos-dark' : delta < 0 ? 'text-indie-neg-dark' : 'text-indie-ink/50'}`}>
                    {delta > 0 ? '+' : ''}{s.fmt(delta)} vs. last mo.
                    {rateDelta != null && (
                      <span className="text-indie-ink/50">
                        {' '}({rateDelta > 0 ? '+' : ''}{pct(rateDelta)})
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-5 pt-4 border-t border-indie-navy/10 grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-indie-ink/55 font-semibold">Cost per call</div>
          <div className="text-lg font-semibold text-indie-navy num-tabular mt-0.5">{moneyFull(c.cost_per_call)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-indie-ink/55 font-semibold">Cost per sale</div>
          <div className="text-lg font-semibold text-indie-navy num-tabular mt-0.5">{moneyFull(c.cost_per_sale)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-indie-ink/55 font-semibold">Show-up rate</div>
          <div className="text-lg font-semibold text-indie-navy num-tabular mt-0.5">{pct(c.show_up_rate)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-indie-ink/55 font-semibold">Conversion (booked→sale)</div>
          <div className="text-lg font-semibold text-indie-navy num-tabular mt-0.5">{pct(c.conversion_rate)}</div>
        </div>
      </div>
      {p && (
        <div className="mt-2 text-[11px] text-indie-ink/50">
          Cobalt bar behind each stage = same day-of-month last month for reference. Green bar = this month.
        </div>
      )}
    </div>
  );
}
